import {Component, OnDestroy, OnInit} from '@angular/core';
import {forkJoin, merge, Observable, of, Subscription, timer} from 'rxjs';
import {MEDIUM_DIALOG, pushOrUpdate, TableColumnDefinition} from '@ft/core';
import {ActivatedRoute} from '@angular/router';
import {assign, without} from 'lodash-es';
import {debounce, map, tap} from 'rxjs/operators';
import {ProductsService} from '../../services/products.service';
import {MatDialog} from '@angular/material/dialog';
import {MatSnackBar} from '@angular/material/snack-bar';
import {PackageExecutionDialog} from '../../dialogs/package-execution/package-execution.dialog';

@Component({
    selector: 'ftm-package-handler',
    templateUrl: './package-handler.component.html',
    styleUrls: [
        './package-handler.component.scss'
    ]
})
export class PackageHandlerComponent implements OnInit, OnDestroy {
    public validDrag: any;
    public canInstall = false;
    public source$: Observable<any[]> = of([]);
    public columns: TableColumnDefinition[] = [
        {label: 'products.version', key: 'version', type: 'text'},
        {label: 'products.added_at', key: 'created_at', type: 'datetime'},
        {label: 'products.packaged_at', key: 'packaged_at', type: 'datetime'},
        {label: 'products.is_installed', key: 'is_installed', type: 'boolean'},
        {label: 'products.is_installable', key: 'is_installable', type: 'boolean'},
        {label: 'products.require_licence', key: 'require_licence', type: 'boolean'},
        {label: 'products.required_versions_rep', key: 'required_versions_rep', type: 'text'},
        {label: 'products.comment', key: 'comment', type: 'text'},
        {
            label: '', key: 'actions', type: 'actions', actions: [
                {
                    class: 'mat-primary',
                    icon: 'mdi-download',
                    tooltip: 'products.execute_install',

                    method: item => this.startExecution(item, false),
                    isDisabled: item => !item.is_installable || item.is_installed || !this.canInstall
                },
                {
                    icon: 'mdi-restart',
                    class: 'mat-primary',
                    tooltip: 'products.update_licence_key',

                    method: item => this.startExecution(item, true),
                    isDisabled: item => !item.is_installed || !this.canInstall
                },
                {
                    class: 'mat-warn',
                    icon: 'mdi-delete',
                    method: item => this.handleDelete(item),
                    isDisabled: item => item.is_installed || !this.canInstall,
                }
            ]
        }

    ];

    private _items: any[] = [];
    private _currentContext: any[];
    private _wsSubscription: Subscription;
    private _routerSubscription: Subscription;

    constructor(
        private _dialog: MatDialog,
        private _snackBar: MatSnackBar,
        private _route: ActivatedRoute,
        private _service: ProductsService
    ) {
    }

    public ngOnInit() {
        this._routerSubscription = merge(this._route.queryParams, this._service.currentProduct$)
            .pipe(
                debounce(() => timer(1)),
                map(() => [
                    this._service.currentProduct$.getValue().id,
                    (this._route.queryParams as any).value.type
                ]),
            )
            .subscribe(data => this._handleContextChanges(data));

        this._wsSubscription = this._service.executionNotifySubscription()
            .subscribe(data => this.canInstall = data);
    }

    public handlePackage(packageFile) {
        this._service.uploadPackage(this._currentContext[0], this._currentContext[1], packageFile)
            .subscribe(
                () => this.source$ = this._updateSource(),
                error => this.handleUploadError(error)
            );
    }

    public ngOnDestroy() {
        this._wsSubscription.unsubscribe();
        this._routerSubscription.unsubscribe();
    }

    private _handleContextChanges(context) {
        this._currentContext = context;
        this.source$ = this._updateSource();
    }

    private _updateSource() {
        return forkJoin([
            this._service.canExecute(),
            this._service.getProductPackages(this._currentContext[0], this._currentContext[1])
        ]).pipe(
            tap(data => this.canInstall = data[0] as boolean),
            map(data => data[1] as any[]),
            tap(data => this._items = data)
        );
    }

    private handleUploadError(response) {
        if (response.status === 422) this._snackBar.open(response.error.msg, null, {duration: 5000});
    }

    private handleDelete(item) {
        this.source$ = this._service.deletePackage(item)
            .pipe(
                map(() => without(this._items, item)),
                tap(items => this._items = items)
            );
    }

    private startExecution(_package, updateOnly) {
        const dialogRef = this._dialog.open(PackageExecutionDialog, assign(MEDIUM_DIALOG, {
            width: '500px',
            disableClose: true,
            data: {package: _package, updateOnly},
        }));

        dialogRef.afterClosed().subscribe(
            data => {
                if (data) {
                    this._items = pushOrUpdate(this._items, data);
                    this.source$ = of(this._items);
                }
            }
        );
    }
}
